// USER-CODE-ORIGIN: every embed here runs a PROJECT's own code, so it mounts
// in the opaque user-code frame (userCodeFrame.js), never in this realm. The
// shell is built exactly as before; only the mount moved. No host registry
// (`hostContext`) and no shared parent objects (`shareContext`) cross: the
// frame reaches the parent only through the consumer's call table (`calls` +
// `allow`), and gets helper CODE only through a `frameKit`.
import { mountUserCode } from './userCodeFrame.js'

export function resolveEmbeddedComponentSchema(schema) {
  if (!schema || typeof schema !== 'object') return schema
  return schema.props?.demoComponent || schema.value || schema
}

export async function embedComponent(iframeNode, data, opts = {}) {
  if (!iframeNode || !data) return
  const {
    key,
    width,
    height,
    fullsize = false,
    // Route mode (canvas PAGE tiles): mount the project the way the published
    // site boots — full app shell, `pages` kept, router ENABLED, initial path
    // = the page's own route — instead of the synthetic single-page shell.
    // Projects routinely drive per-route content through their OWN router
    // hooks and state (natali/shop keys every section off `activeSection`,
    // set by its routing); the isolated shell mounts the structure but none
    // of that state machinery, so the tile renders a 0-height page with all
    // sections hidden. Route mode reproduces the real boot, so whatever the
    // published page does, the tile does.
    routeMode = false,
    // The frame -> parent call table (userCodeFrame.js): `{ name: handler }`
    // plus an `allow(name, args)` policy, and the frame kit (helper code).
    calls = {},
    allow = null,
    frameKit = null,
    // (message, code) => void — a boot error, a project error, or the frame
    // stopped for reloading in a loop (`user_code_reload_loop`)
    onError = null,
    // the host element (its `scope` holds the frame host memo)
    el = null
  } = opts
  if (!key) return

  if (routeMode && key.startsWith('/') && data.pages && Object.hasOwn(data.pages, key)) {
    // Full-app mount at the page's route. No `pages: undefined` / `router:
    // false` overrides — the framework's auto-router matches the path set by
    // the frame's initial path (`router.initialPath`) and runs the page's real
    // lifecycle (onRenderRouter, per-route state seeding, …).
    const appShell = {
      ...(data.app && typeof data.app === 'object' ? data.app : {}),
      boxSize: '100dvh 100dvw',
      overflow: 'auto',
      theme: 'document'
    }
    return mountUserCode(iframeNode, data, appShell, {
      path: key,
      calls,
      allow,
      frameKit,
      onError,
      el,
      logTag: key
    })
  }

  // Resolve the component/page schema off the raw project data before we
  // hand it to the frame mount (it serializes into the frame
  // realm, so resolving after would operate on a copy — equivalent but
  // needlessly indirect).
  // Pages may be keyed with OR without a leading slash ('about' as well as
  // '/about'), so detect by where the key actually lives, not just the '/'
  // prefix — otherwise a slash-less page key falls through to components and
  // renders the "not registered" fallback. (2026-06-17.)
  const schema = data.pages?.[key] || data.components?.[key]
  const factory = data.pages?.[key] ? 'pages' : 'components'

  const hasExplicitBox = width != null || height != null
  const resolvedSchema = resolveEmbeddedComponentSchema(schema)
  const inner = resolvedSchema
    ? fullsize
      ? { ...resolvedSchema, boxSize: '100dvh 100dvw' }
      : resolvedSchema
    : {
        tag: 'div',
        text: `No ${factory.slice(0, -1)} registered for key "${key}"`,
        style: { padding: '24px', font: '14px system-ui', color: '#999' }
      }

  // DOMQL treats `/…` child keys as router paths rather than components,
  // so a page whose key starts with `/` (e.g. `/transactions`) wouldn't
  // mount under its own key. Wrap it under a valid identifier and let
  // the page schema itself render underneath.
  const shellKey = schema ? (key.startsWith('/') ? 'Page' : key) : 'Fallback'
  const shell = {
    flow: 'x',
    align: 'center center',
    boxSize: hasExplicitBox ? `${height || '100dvh'} ${width || '100dvw'}` : '100dvh 100dvw',
    overflow: 'auto',
    theme: 'document',
    // Hoist `data.app.fetch` onto the root shell. The fetch plugin reads
    // `element.fetch` in its `create` hook; without this, embedded pages
    // mount but their project-level queries never fire (canvas tiles show
    // "Loading…" while preview renders real data through the same data).
    ...(data.app?.fetch ? { fetch: data.app.fetch } : {}),
    [shellKey]: inner
  }

  const contextOverrides = {
    pages: undefined,
    app: undefined,
    router: false
  }

  // PAGE tiles must mount at the page's OWN route. Projects routinely gate
  // sections on the current path (`hide: (el, s) => s.root.currentPath !== …`,
  // router-driven show/hide over one shared Body) — under the previous
  // hardcoded `/component/<key>` path every such gate evaluated hidden and a
  // page tile rendered as a 0-height, all-sections-display:none document:
  // structure and copy present in the DOM, nothing visible (measured live on
  // natali/shop, 2026-07-29). Components keep the synthetic /component/ path —
  // they are not routes.
  const framePath = factory === 'pages' && key.startsWith('/') ? key : '/component/' + key

  return mountUserCode(iframeNode, data, shell, {
    path: framePath,
    contextOverrides,
    calls,
    allow,
    frameKit,
    onError,
    el,
    logTag: key
  })
}
